import { useMemo } from 'react';

import { useTranslation } from '@kc-console/core';
import { TooltipButton } from '@kc-console/shared-ui';
import { IllustStatusStartAgent } from '@kep/kcds-icons';

import ExceptionBoundary from './components/ExceptionBoundary';

import type { PropsWithChildren, ReactNode } from 'react';
import type { ExceptionBoundaryProps } from './components/ExceptionBoundary';

export interface PageAgentBoundaryProps
  extends Omit<ExceptionBoundaryProps, 'title' | 'description' | 'actions'> {
  handleStart: () => void;
  isActivating: boolean;
  description?: string;
  startServiceButtonTooltip?: ReactNode;
  title?: string;
}

/**
 * 서비스 에이전트 설치 안내 컴포넌트
 * @description
 * - 에이전트 설치가 필요한 서비스에서 미활성화 시 설치 안내 화면을 표시합니다.
 * @example
 * <PageAgentBoundary
 *   handleStart={handleInstallAgent}
 *   isLoading={isLoading}
 *   isActivating={isActivating}
 *   isValid={isAgentInstalled}
 * >
 *   <AgentRequiredServiceContent />
 * </PageAgentBoundary>
 */
export default function PageAgentBoundary({
  children,
  title,
  description,
  handleStart,
  isActivating,
  startServiceButtonTooltip,
  ...props
}: PropsWithChildren<PageAgentBoundaryProps>) {
  const { t } = useTranslation();
  const pageTitle = title || t('layout:exception_page.start_service.title');
  const pageDescription = useMemo(() => {
    return (
      description || (
        <>
          {t('layout:exception_page.no_agent_boundary.message')}
          <br />
          {t('layout:exception_page.no_agent_boundary.instruction')}
        </>
      )
    );
  }, [description, t]);

  const actions = useMemo(
    () => (
      <TooltipButton
        message={startServiceButtonTooltip}
        variant="primary"
        size="lg"
        disabled={isActivating}
        onClick={() => handleStart()}
      >
        {t('layout:exception_page.start_service')}
      </TooltipButton>
    ),
    [isActivating, handleStart, t, startServiceButtonTooltip],
  );

  return (
    <ExceptionBoundary
      title={pageTitle}
      description={pageDescription}
      illustration={<IllustStatusStartAgent />}
      actions={actions}
      {...props}
    >
      {children}
    </ExceptionBoundary>
  );
}
